import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Lock,
  Play,
  Square,
  RefreshCw,
  HardDrive,
  ImageIcon,
  Archive,
  Sparkles,
} from "lucide-react";
import { devLog } from "../lib/devLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Defaults for the sweep loop — tuned to complete one batch within
// ~30 s so the UI feels responsive and the reverse proxy never trips
// a request timeout.
const BATCH_LIMIT = 30;
const BATCH_CONCURRENCY = 6;
const BATCH_PACING_MS = 300;

const fmtBytes = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

const Stat = ({ label, value, hint, testId, accent }) => (
  <div
    className="border border-[#2C2621]/12 bg-[#FDFBF7] p-5"
    data-testid={testId}
  >
    <p className="overline text-[10px] tracking-[0.22em] text-[#5C5248]">{label}</p>
    <p
      className={`mt-2 font-serif-x text-3xl tabular-nums ${
        accent ? "text-[#C16542]" : "text-[#2C2621]"
      }`}
    >
      {value}
    </p>
    {hint ? <p className="mt-1 text-xs text-[#5C5248]">{hint}</p> : null}
  </div>
);

const AdminStoragePage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [totals, setTotals] = useState({
    batches: 0,
    optimized: 0,
    skipped: 0,
    errors: 0,
    savedBytes: 0,
  });
  // Ref so the bash-stop button can flip without re-running the loop.
  const stopRef = useRef(false);

  useEffect(() => {
    // Verify existing session cookie on mount.
    let cancelled = false;
    axios
      .get(`${API}/admin/session/verify`)
      .then(() => {
        if (cancelled) return;
        void fetchStats().then(() => setUnlocked(true)).catch(() => setUnlocked(false));
      })
      .catch(() => {
        if (!cancelled) setUnlocked(false);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchStats = async () => {
    const res = await axios.get(`${API}/admin/db/storage-stats`);
    setStats(res.data);
    return res.data;
  };

  const tryUnlock = async (pwd) => {
    setLoading(true);
    try {
      // Login → sets httpOnly cookie server-side.
      await axios.post(`${API}/admin/session/login`, { password: pwd });
      await fetchStats();
      setUnlocked(true);
      setPassword(""); // clear from React state — cookie carries auth from here.
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Wrong password");
      else if (e?.response?.status === 429) toast.error("Too many attempts — try again shortly");
      else toast.error("Unable to log in");
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  // ── Sweep loop ────────────────────────────────────────────────────────
  const runSweep = async ({ dryRun = false } = {}) => {
    stopRef.current = false;
    setRunning(true);
    setTotals({ batches: 0, optimized: 0, skipped: 0, errors: 0, savedBytes: 0 });
    setLastBatch(null);
    try {
      // Loop until remaining == 0 or the user clicks Stop.
      for (let i = 0; i < 10_000; i += 1) {
        if (stopRef.current) break;
        let resp;
        try {
          resp = await axios.post(
            `${API}/admin/db/optimize-legacy-images?limit=${BATCH_LIMIT}&concurrency=${BATCH_CONCURRENCY}${
              dryRun ? "&dry_run=true" : ""
            }`,
            null,
            { timeout: 240_000 },
          );
        } catch (err) {
          toast.error(`Batch failed: ${err?.response?.status || err.message}`);
          break;
        }
        const b = resp.data || {};
        setLastBatch(b);
        setTotals((prev) => ({
          batches: prev.batches + 1,
          optimized: prev.optimized + (b.optimized || 0),
          skipped: prev.skipped + (b.skipped || 0),
          errors: prev.errors + (b.errors || 0),
          savedBytes: prev.savedBytes + (b.saved_bytes || 0),
        }));
        // Refresh stats every 3 batches so the progress bar reflects DB state.
        if (i % 3 === 0) {
          try {
            await fetchStats();
          } catch (err) {
            devLog.debug("[AdminStorage] mid-sweep stats refresh failed", err);
            /* non-fatal */
          }
        }
        if ((b.remaining ?? 0) === 0) {
          toast.success("Sweep complete — every image is optimized 🎉");
          break;
        }
        // Light pacing keeps the bucket happy and avoids burst rate limits.
        await new Promise((r) => setTimeout(r, BATCH_PACING_MS));
      }
    } finally {
      setRunning(false);
      // Final refresh so the post-sweep numbers are accurate.
      try {
        await fetchStats();
      } catch (err) {
        devLog.debug("[AdminStorage] final stats refresh failed", err);
        /* non-fatal */
      }
    }
  };

  const stopSweep = () => {
    stopRef.current = true;
    toast("Stopping after current batch…", { icon: "⏸" });
  };

  // ─────────── PASSWORD GATE ─────────────────────────────────────────────
  if (!unlocked) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-[#FDFBF7] px-6 py-20">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void tryUnlock(password);
          }}
          className="w-full max-w-md bg-[#FDFBF7] border border-[#2C2621]/15 p-8"
          data-testid="admin-storage-gate"
        >
          <Lock className="w-5 h-5 text-[#C16542]" strokeWidth={1.6} />
          <h1 className="font-serif-x mt-3 text-3xl text-[#2C2621]">Storage</h1>
          <p className="mt-1 text-sm text-[#5C5248]">
            Live bucket stats &amp; optimization controls.
          </p>
          <label className="block overline mt-6 mb-2">Admin password</label>
          <input
            autoFocus
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent outline-none border border-[#2C2621]/20 focus:border-[#C16542] px-4 py-3 text-[#2C2621]"
            data-testid="admin-storage-password-input"
          />
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-6 inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-7 py-3 text-xs tracking-[0.22em] uppercase"
            data-testid="admin-storage-password-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-3.5 h-3.5" />
            )}
            Unlock
          </button>
        </form>
      </div>
    );
  }

  // ─────────── DASHBOARD ────────────────────────────────────────────────
  const imgs = stats?.images || {};
  const pct = imgs.optimized_pct ?? 0;
  const liveRemaining = imgs.pending_files ?? 0;

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] text-[#2C2621] px-6 md:px-10 py-12"
      data-testid="admin-storage-page"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="overline">CMS · Operations</p>
            <h1 className="font-serif-x text-4xl md:text-5xl tracking-tight">
              Storage
            </h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Live snapshot of every file in the{" "}
              <code className="bg-[#FAF6EE] px-1.5 py-0.5 text-xs">
                xaluca-cms
              </code>{" "}
              bucket and a one-click sweep to bring legacy uploads up to the
              current Pillow optimization standard (max-width 2560, format
              preserved, EXIF stripped).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] transition-colors disabled:opacity-60"
              data-testid="admin-storage-refresh"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Refresh
            </button>
            {running ? (
              <button
                type="button"
                onClick={stopSweep}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase bg-[#2C2621] hover:bg-[#1a1612] text-[#FDFBF7]"
                data-testid="admin-storage-stop-sweep"
              >
                <Square className="w-3.5 h-3.5" />
                Stop
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => runSweep({ dryRun: true })}
                  disabled={running || liveRemaining === 0}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] transition-colors disabled:opacity-60"
                  data-testid="admin-storage-dry-run"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Dry run
                </button>
                <button
                  type="button"
                  onClick={() => runSweep({ dryRun: false })}
                  disabled={running || liveRemaining === 0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7]"
                  data-testid="admin-storage-run-sweep"
                >
                  <Play className="w-3.5 h-3.5" />
                  Run sweep
                </button>
              </>
            )}
          </div>
        </div>

        {/* Top-line stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat
            label="Bucket total"
            value={fmtBytes(stats?.totals?.bytes)}
            hint={`${stats?.totals?.files ?? 0} files`}
            testId="stat-bucket-total"
          />
          <Stat
            label="Images"
            value={fmtBytes(imgs.bytes)}
            hint={`${imgs.files ?? 0} files`}
            testId="stat-images-total"
          />
          <Stat
            label="Already optimized"
            value={`${imgs.optimized_files ?? 0}`}
            hint={`${fmtBytes(imgs.optimized_bytes)} · ${pct}%`}
            testId="stat-optimized"
          />
          <Stat
            label="Pending"
            value={`${imgs.pending_files ?? 0}`}
            hint={fmtBytes(imgs.pending_bytes)}
            testId="stat-pending"
            accent
          />
        </div>

        {/* Progress bar */}
        <div className="border border-[#2C2621]/12 bg-[#FDFBF7] p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="overline text-[10px] tracking-[0.22em] text-[#5C5248]">
              Optimization progress
            </p>
            <p className="text-xs tabular-nums text-[#5C5248]">
              {imgs.optimized_files ?? 0} / {imgs.files ?? 0} ({pct}%)
            </p>
          </div>
          <div className="relative h-3 bg-[#FAF6EE] border border-[#2C2621]/10 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#C16542] transition-[width] duration-700"
              style={{ width: `${pct}%` }}
              data-testid="admin-storage-progress-fill"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-[#5C5248]">
            <span className="inline-flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" />
              Saved so far:&nbsp;
              <strong className="text-[#C16542] tabular-nums">
                {fmtBytes(imgs.saved_bytes)}
              </strong>
            </span>
            {imgs.original_mb_processed ? (
              <span className="text-xs">
                {fmtBytes(imgs.original_bytes_processed)} ⟶{" "}
                {fmtBytes(imgs.optimized_bytes)} on the rows touched
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5">
              <Archive className="w-3 h-3" />
              Audio:&nbsp;{fmtBytes(stats?.audio?.bytes)} ({stats?.audio?.files ?? 0})
            </span>
          </div>
        </div>

        {/* Live sweep panel (only shows while running or after a completed batch) */}
        {(running || lastBatch) && (
          <div
            className="border border-[#C16542]/30 bg-[#FAF6EE] p-5 mb-6"
            data-testid="admin-storage-sweep-panel"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
              <p className="overline text-[10px] tracking-[0.22em] text-[#C16542]">
                {running ? "Sweep running…" : "Last sweep result"}
              </p>
              {running ? (
                <span className="inline-flex items-center gap-2 text-xs text-[#5C5248]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Batch {totals.batches} · {BATCH_LIMIT} files per batch ·
                  concurrency {BATCH_CONCURRENCY}
                </span>
              ) : null}
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <Stat
                label="Batches"
                value={totals.batches}
                hint={lastBatch?.dry_run ? "dry run" : "live"}
                testId="sweep-batches"
              />
              <Stat
                label="Files optimized"
                value={totals.optimized}
                hint={`+ ${totals.skipped} skipped`}
                testId="sweep-optimized"
              />
              <Stat
                label="Errors"
                value={totals.errors}
                hint={totals.errors ? "will retry next sweep" : "all clean"}
                testId="sweep-errors"
              />
              <Stat
                label="Saved this sweep"
                value={fmtBytes(totals.savedBytes)}
                hint={
                  lastBatch?.remaining != null
                    ? `${lastBatch.remaining} remaining`
                    : ""
                }
                testId="sweep-saved"
                accent
              />
            </div>
          </div>
        )}

        {/* Footer help text */}
        <div className="text-xs text-[#5C5248] flex items-start gap-2 mt-8">
          <HardDrive className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
          <p>
            The sweep is fully idempotent — already-optimized rows are skipped
            automatically. Errors are usually transient bucket I/O hiccups and
            will be retried the next time the sweep encounters them. You can
            stop &amp; resume at any time without losing progress.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminStoragePage;
